import React, { useState, useEffect, useCallback } from "react";
import { Spin, Tooltip, message, Input, DatePicker, Select } from "antd";
import axiosInstance from "../../axios/axiosInstance";
import PatientFormModal from "./components/PatientFormModal";
import { Link } from "react-router-dom";
import {
    PencilSquareIcon,
    DocumentTextIcon,
    MagnifyingGlassIcon,
    PlusIcon,
} from "@heroicons/react/24/outline";
import { debounce } from "lodash";
import {
    ActionButton,
    DataTable,
    PageCard,
    PageShell,
    PageToolbar,
    RowIconButton,
    VisitStatusBadge,
} from "../../components/ui";
import { useTranslation } from "react-i18next";

const Patients = () => {
    const { t } = useTranslation();
    const [patients, setPatients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentPatient, setCurrentPatient] = useState(null);
    const [birthDateFilter, setBirthDateFilter] = useState(null);
    const [provinces, setProvinces] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [selectedProvince, setSelectedProvince] = useState(null);
    const [selectedDistrict, setSelectedDistrict] = useState(null);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0
    });

    const fetchPatients = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const token = localStorage.getItem('token');
                const params = {
                    page: pagination.current,
                    pageSize: pagination.pageSize,
                };
                if (searchQuery.trim()) {
                    params.name = searchQuery;
                }
                if (birthDateFilter) {
                    params.birth_at = birthDateFilter.format('YYYY');
                }
                if (selectedDistrict) {
                    params.district_id = selectedDistrict;
                }

                const response = await axiosInstance.get(`/patients`, {
                    params,
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json; charset=utf-8',
                    },
                });

                setPatients(response.data.data);
                setPagination((prev) => ({
                    ...prev,
                    current: response.data.meta.current_page,
                    total: response.data.meta.total,
                }));
            } catch (error) {
                console.error('Ошибка при получении пациентов:', error);
            } finally {
                setIsLoading(false);
            }
        },
        [pagination.current, pagination.pageSize, searchQuery, birthDateFilter, selectedDistrict]
    );

    const fetchProvinces = async () => {
        try {
            const res = await axiosInstance.get('/global/province');
            setProvinces(res.data.data);
        } catch (error) {
            console.error('Error fetching provinces:', error);
        }
    };

    const fetchDistricts = async (provinceId) => {
        try {
            const res = await axiosInstance.get(`/global/province/${provinceId}`);
            setDistricts(res.data.data.districts);
        } catch (error) {
            console.error('Error fetching districts:', error);
        }
    };

    const handleProvinceChange = (provinceId) => {
        setSelectedProvince(provinceId);
        setSelectedDistrict(null);
        setPagination((prev) => ({ ...prev, current: 1 }));
        if (provinceId) {
            fetchDistricts(provinceId);
        } else {
            setDistricts([]);
        }
    };

    const handleDistrictChange = (districtId) => {
        setSelectedDistrict(districtId);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const debouncedSearch = useCallback(
        debounce((value) => {
            setSearchQuery(value);
            setPagination((prev) => ({
                ...prev,
                current: 1,
            }));
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        debouncedSearch(e.target.value);
    };

    useEffect(() => {
        fetchPatients();
        fetchProvinces();
    }, [fetchPatients]);

    const handleTableChange = (newPagination) => {
        setPagination((prev) => ({
            ...prev,
            current: newPagination.current,
        }));
    };

    const highlightText = (text, search) => {
        if (!search) return text;

        const regex = new RegExp(`(${search})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, index) =>
            regex.test(part) ? (
                <span key={index} className="bg-indigo-100 text-[#1D2C7B] font-semibold rounded px-0.5">
                    {part}
                </span>
            ) : (
                part
            )
        );
    };

    const handleOpenUpdateDialog = (patient) => {
        setCurrentPatient(patient);
        setIsModalVisible(true);
    };

    const handleClose = () => {
        setIsModalVisible(false);
        setCurrentPatient(null);
    };

    const handleSubmit = async (values) => {
        setIsLoading(true);
        const formData = new FormData();

        let phone = values.phone.replace(/\D/g, "");
        if (phone.length === 13) {
            phone = phone.slice(0, 12);
        }

        formData.append('name', values.name);
        formData.append('district_id', values.location.district_id);
        formData.append('partner_id', values.partner_id);
        if (values.pinfl) {
            formData.append('pinfl', values.pinfl);
        }
        formData.append('phone', phone);
        formData.append('profession', values.profession);
        formData.append('gender', values.gender);
        formData.append('home_address', values.home_address);
        formData.append('work_address', values.work_address);
        formData.append('remark', values.remark);
        if (values.file) {
            formData.append('file', values.file.file);
        }
        formData.append('birth_at', values.birth_at.format('YYYY-MM-DD'));

        try {
            if (currentPatient) {
                await axiosInstance.put(`/patients/${currentPatient.id}`, formData, {
                    headers: {
                        'Content-Type': 'application/json; charset=utf-8'
                    }
                });
                fetchPatients(pagination.current);
                message.success(t('patients.updated'));
            } else {
                await axiosInstance.post('/patients', formData);
                fetchPatients(pagination.current);
                message.success(t('patients.created'));
            }
            handleClose();
        } catch (error) {
            message.error(t('patients.saveError'));
            console.error('Error saving patient:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const formatPhone = (phone) => {
        if (!phone || phone.length !== 12) return phone;
        return `+998 (${phone.slice(3, 5)}) ${phone.slice(5, 8)}-${phone.slice(8, 10)}-${phone.slice(10, 12)}`;
    };

    const columns = [
        {
            title: t('patients.colId'),
            dataIndex: "id",
            key: "id",
            width: 72,
            sorter: (a, b) => a.id.localeCompare(b.id),
            render: (id) => <span className="text-slate-500 tabular-nums text-xs">{id}</span>,
        },
        {
            title: t('patients.colName'),
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text, record) => (
                <div className="flex items-center gap-3 min-w-[180px]">
                    <img
                        src={record.avatar}
                        alt=""
                        className="h-8 w-8 rounded-full border border-slate-200 bg-slate-50 object-cover shrink-0"
                    />
                    <div className="min-w-0">
                        <Link
                            to={`/patient/${record.id}`}
                            className="block text-sm font-medium text-[#1D2C7B] hover:underline truncate"
                        >
                            {highlightText(text, searchQuery)}
                        </Link>
                        <span className="block text-xs text-slate-500 mt-0.5">{t('common.code')}: {record.code}</span>
                    </div>
                </div>
            ),
        },
        {
            title: t('patients.colBirth'),
            dataIndex: "birth_at",
            key: "birth_at",
            width: 130,
            sorter: (a, b) => new Date(a.birth_at) - new Date(b.birth_at),
            render: (v) => <span className="text-sm text-slate-700">{v}</span>,
        },
        {
            title: t('patients.colStatus'),
            dataIndex: "visit_status",
            key: "visit_status",
            width: 200,
            render: (visitStatus) => <VisitStatusBadge status={visitStatus} />,
        },
        {
            title: t('patients.colAddress'),
            dataIndex: "home_address",
            key: "home_address",
            ellipsis: true,
            render: (v) => <span className="text-sm text-slate-600">{v || t('common.dash')}</span>,
        },
        {
            title: t('patients.colPhone'),
            dataIndex: "phone",
            key: "phone",
            width: 178,
            render: (phone) => (
                <span className="text-xs sm:text-sm text-slate-700 tabular-nums whitespace-nowrap">
                    {formatPhone(phone)}
                </span>
            ),
        },
        {
            title: t('patients.colActions'),
            key: "action",
            width: 88,
            fixed: 'right',
            render: (_, record) => (
                <div className="flex items-center gap-0.5">
                    <Tooltip title={t('patients.card')}>
                        <Link
                            to={`/patient/${record.id}`}
                            className="inline-flex p-1.5 rounded-md text-slate-400 hover:text-[#1D2C7B] hover:bg-slate-100 transition-colors"
                        >
                            <DocumentTextIcon className="w-[18px] h-[18px]" />
                        </Link>
                    </Tooltip>
                    <Tooltip title={t('patients.edit')}>
                        <RowIconButton onClick={() => handleOpenUpdateDialog(record)}>
                            <PencilSquareIcon className="w-[18px] h-[18px]" />
                        </RowIconButton>
                    </Tooltip>
                </div>
            ),
        },
    ];

    const renderMobileCard = (record) => (
        <div>
            <div className="flex items-start gap-3">
                <img
                    src={record.avatar}
                    alt=""
                    className="h-10 w-10 rounded-full border border-slate-200 bg-slate-50 object-cover shrink-0"
                />
                <div className="min-w-0 flex-1">
                    <Link
                        to={`/patient/${record.id}`}
                        className="block text-sm font-semibold text-[#1D2C7B] truncate"
                    >
                        {highlightText(record.name, searchQuery)}
                    </Link>
                    <span className="block text-xs text-slate-500 mt-0.5">{t('common.code')}: {record.code}</span>
                </div>
                <RowIconButton onClick={() => handleOpenUpdateDialog(record)} aria-label={t('patients.edit')}>
                    <PencilSquareIcon className="w-[18px] h-[18px]" />
                </RowIconButton>
            </div>

            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-slate-600">
                <VisitStatusBadge status={record.visit_status} />
                <span className="tabular-nums">{record.birth_at}</span>
                <a href={`tel:+${record.phone}`} className="tabular-nums text-[#1D2C7B]">
                    {formatPhone(record.phone)}
                </a>
            </div>

            {record.home_address && (
                <p className="mt-1.5 mb-0 text-xs text-slate-500 line-clamp-2">{record.home_address}</p>
            )}
        </div>
    );

    return (
        <PageShell>
            <PageCard>
                <PageToolbar
                    title={t('patients.title')}
                    count={pagination.total}
                    filters={
                        <>
                            <Input
                                allowClear
                                size="small"
                                prefix={<MagnifyingGlassIcon className="w-4 h-4 text-slate-400" />}
                                placeholder={t('patients.search')}
                                onChange={handleSearchChange}
                                className="md:!w-[180px]"
                            />
                            <Select
                                size="small"
                                placeholder={t('common.province')}
                                className="md:min-w-[130px]"
                                value={selectedProvince}
                                onChange={handleProvinceChange}
                                allowClear
                            >
                                {provinces.map((prov) => (
                                    <Select.Option key={prov.id} value={prov.id}>{prov.name}</Select.Option>
                                ))}
                            </Select>
                            <Select
                                size="small"
                                placeholder={t('common.district')}
                                className="md:min-w-[130px]"
                                value={selectedDistrict}
                                onChange={handleDistrictChange}
                                disabled={!selectedProvince}
                                allowClear
                            >
                                {districts.map((dist) => (
                                    <Select.Option key={dist.id} value={dist.id}>{dist.name}</Select.Option>
                                ))}
                            </Select>
                            <DatePicker
                                size="small"
                                picker="year"
                                placeholder={t('common.year')}
                                value={birthDateFilter}
                                onChange={(date) => {
                                    setBirthDateFilter(date);
                                    setPagination((prev) => ({ ...prev, current: 1 }));
                                }}
                                className="md:!w-[88px]"
                            />
                        </>
                    }
                    actions={
                        <ActionButton
                            icon={<PlusIcon className="w-4 h-4" />}
                            onClick={() => setIsModalVisible(true)}
                        >
                            {t('patients.new')}
                        </ActionButton>
                    }
                />

                <Spin spinning={isLoading}>
                    <DataTable
                        dataSource={patients}
                        columns={columns}
                        mobileCard={renderMobileCard}
                        pagination={{
                            ...pagination,
                            showSizeChanger: false,
                            position: ['bottomCenter'],
                            className: 'px-4 pb-3',
                            showTotal: (total, range) => (
                                <span className="text-xs text-slate-500">
                                    {t('common.perPage', { from: range[0], to: range[1], total })}
                                </span>
                            ),
                        }}
                        onChange={handleTableChange}
                    />
                </Spin>
            </PageCard>

            <PatientFormModal
                visible={isModalVisible}
                onClose={handleClose}
                onSubmit={handleSubmit}
                loading={isLoading}
                initialValues={currentPatient}
            />
        </PageShell>
    );
};

export default Patients;
